import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import {
  ListBoxItem as AriaListBoxItem,
  type ListBoxItemProps as AriaListItemProps,
  composeRenderProps,
} from 'react-aria-components';
import { Skeleton } from '../skeleton';

type LeveledItem = { level: string; id: string };

interface ListBoxItemProps extends Omit<AriaListItemProps, 'selectionMode'> {
  /**
   * Whether the item is loading.
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  isLoading?: boolean;
  /**
   * The display variant of the item.
   *
   * @optional
   * @default 'default'
   * @type 'default'
   */
  level?: 'default';
  /**
   * Array of subcategory levels to display above the main text.
   * @type {string[]}
   */
  levels?: string[];
}

const itemClasses = cva({
  base: cx(
    'relative flex min-h-18 items-center gap-4 mx-3 py-4 px-4 cursor-pointer select-none',
    'text-md text-primary',
    'outline-none transition-colors',
    'data-[disabled]:pointer-events-none data-[disabled]:text-disabled',
    'data-[focused]:bg-primary-hover',
    'data-[selected]:bg-primary-hover',
  ),
  variants: {
    level: {
      default: 'text-primary',
    },
  },
  defaultVariants: {
    level: 'default',
  },
});

function ListBoxItem(props: ListBoxItemProps, ref: React.ForwardedRef<HTMLDivElement>) {
  const {
    children,
    className: classNameProp,
    isDisabled,
    isLoading,
    level = 'default',
    textValue: textValueProp,
    levels,
    id,
    ...other
  } = props;

  // Avoids accessibility warnings:
  // "A `textValue` prop is required for <ListBoxItem> elements with non-plain text
  // children in order to support accessibility features such as type to select."
  //
  // We were still getting this warning with plain text children due to the use of an arrow function below.
  const textValue = React.useMemo(() => {
    if (!isLoading && typeof children === 'string' && !textValueProp) return children;
    return textValueProp;
  }, [isLoading, children, textValueProp]);

  return (
    <AriaListBoxItem
      {...other}
      id={id}
      ref={ref}
      aria-label={isLoading ? 'Loading' : undefined}
      aria-live={isLoading ? 'polite' : undefined}
      className={composeRenderProps(classNameProp, (className, renderProps) =>
        cx(
          'vzt-list-box__item',
          'group',
          itemClasses({ ...renderProps, level, className }),
          'flex-col items-start justify-center',
        ),
      )}
      data-loading={isLoading || undefined}
      isDisabled={isLoading || isDisabled}
      textValue={textValue}
    >
      {(values) => {
        if (isLoading) return <Skeleton className="w-full h-8" />;
        return (
          <>
            {Array.isArray(levels) && levels.length > 0 && (
              <div className="text-xs text-quaternary">
                {levels.map((level, index) => (
                  <span key={`${id}-${index}-${level}`}>
                    {level}
                    {index < levels.length - 1 && <span className="mx-3">&bull;</span>}
                  </span>
                ))}
              </div>
            )}
            <div className="text-md text-primary">{typeof children === 'function' ? children(values) : children}</div>
          </>
        );
      }}
    </AriaListBoxItem>
  );
}

const _ListBoxItem = React.forwardRef<HTMLDivElement, ListBoxItemProps>(ListBoxItem);

export { _ListBoxItem as ListBoxItem };
export type { ListBoxItemProps, LeveledItem };
